<template>
  <v-container fluid class="mt-0">
    <v-snackbar
      v-model="snack"
      :timeout="4000"
      top
      :color="snackColor"
      class="text-capitalize"
    >
      {{ snackText }}
      <v-btn @click="snack = false" outlined>
        <v-icon>close</v-icon>
      </v-btn>
    </v-snackbar>

    <v-data-table
      :headers="headers"
      :items="accounts"
      :options.sync="options"
      :server-items-length="totalAccounts"
      class="elevation-1"
      :loading="loading"
    >
      <template v-slot:top>
        <v-toolbar flat>
          <v-toolbar-title>Accounts</v-toolbar-title>
          <v-divider class="mx-4" inset vertical></v-divider>
          <v-spacer></v-spacer>
          <v-dialog v-model="dialog" max-width="500px">
            <template v-slot:activator="{ on }">
              <v-btn small dark class="mb-2" v-on="on"
                ><v-icon>add</v-icon></v-btn
              >
            </template>

            <v-card>
              <v-card-title>
                <span class="headline">{{ formTitle }}</span>
              </v-card-title>
              <v-card-text>
                <v-form wrap ref="accountForm" v-model="valid">
                  <v-container fluid>
                    <v-text-field
                      v-model="editedAccount.name"
                      label="Name"
                      :rules="nameRules"
                    ></v-text-field>
                    <v-text-field
                      v-model="editedAccount.email"
                      label="Email"
                      :rules="emailRules"
                    ></v-text-field>
                    <v-switch
                      v-model="editedAccount.active"
                      label="Active"
                      inset
                    ></v-switch>
                    <v-checkbox
                      multiple
                      v-model="editedAccount.roles"
                      label="Admin"
                      value="admin"
                    ></v-checkbox>
                    <v-checkbox
                      multiple
                      v-model="editedAccount.roles"
                      label="User"
                      value="user"
                    ></v-checkbox>
                  </v-container>
                </v-form>
              </v-card-text>

              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn text @click="close">Cancel</v-btn>
                <v-btn color="primary" @click="save" :disabled="!valid"
                  >Save</v-btn
                >
              </v-card-actions>
            </v-card>
          </v-dialog>
        </v-toolbar>
      </template>

      <template v-slot:item.active="{ item }">
        <v-icon small>{{ item.active ? 'done' : 'block' }}</v-icon>
      </template>
      <template v-slot:item.admin="{ item }">
        <v-icon v-if="item.roles.includes('admin')" small>security</v-icon>
      </template>
      <template v-slot:item.last_login="{ item }">
        {{ item.last_login | formatDate }}
      </template>

      <template v-slot:item.action="{ item }">
        <v-icon small class="mr-2" @click="editAccount(item)">edit</v-icon>
        <v-icon small @click="deleteAccount(item)">delete</v-icon>
      </template>
    </v-data-table>
  </v-container>
</template>

<script>
import { AdminService } from '@/services/admin.service'

export default {
  name: 'accounts',
  data() {
    return {
      accounts: [],
      totalAccounts: 0,
      loading: false,
      options: {},
      headers: [
        { text: 'Active', value: 'active' },
        { text: 'Admin', value: 'admin', sortable: false },
        { text: 'Name', value: 'name', align: 'left' },
        { text: 'Email', value: 'email', align: 'left' },
        { text: 'Last Login', value: 'last_login', align: 'left' },
        { text: 'Actions', value: 'action', sortable: false }
      ],
      dialog: false,
      editedIndex: -1,
      editedAccount: {
        name: '',
        email: '',
        roles: ['user'],
        active: true
      },
      valid: true,
      defaultAccount: {
        name: '',
        email: '',
        roles: ['user'],
        active: true
      },
      errors: {},
      snack: false,
      snackColor: '',
      snackText: '',
      nameRules: [
        v => !!v || 'Name is required',
        v => (v && v.length <= 100) || 'Name must be less than 100 characters',
        () => !this.errors.name || this.errors.name
      ],
      emailRules: [
        v => !!v || 'E-mail is required',
        v =>
          /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(v) ||
          'E-mail must be valid',
        () => !this.errors.email || this.errors.email
      ]
    }
  },
  watch: {
    dialog(val) {
      val || this.close()
    },
    options: {
      async handler() {
        const { accounts, count } = await this.getAccounts()
        this.accounts = accounts
        this.totalAccounts = count
      },
      depp: true
      // immediate: true
    }
  },
  computed: {
    formTitle() {
      return this.editedIndex === -1 ? 'New Account' : 'Edit Account'
    }
  },
  methods: {
    async getAccounts() {
      this.loading = true
      try {
        const { sortBy, descending, page, itemsPerPage } = this.options
        const filter = {
          page: page,
          limit: itemsPerPage === -1 ? 0 : itemsPerPage,
          order: (sortBy ? sortBy : '') + (descending ? ' desc' : '')
        }

        let { accounts, count } = await AdminService.getAccounts(filter)
        return { accounts, count }
      } catch (error) {
        console.log('could not get accounts data', error)
      } finally {
        this.loading = false
      }
    },

    editAccount(account) {
      this.editedIndex = this.accounts.indexOf(account)
      this.editedAccount = Object.assign({}, account)
      this.dialog = true
    },
    close() {
      this.dialog = false
      this.editedIndex = -1
      this.editedAccount = Object.assign({}, this.defaultAccount)
    },

    async save() {
      if (this.editedIndex > -1) {
        try {
          const account = await AdminService.updateAccount(
            Object.assign({}, this.editedAccount)
          )
          Object.assign(this.accounts[this.editedIndex], account)
          this.close()
          this.snackSuccess('account updated')
        } catch (error) {
          this.errors = { ...error.fields }
          this.$refs.accountForm.validate()
          this.snackError(error.message)
        }
      } else {
        try {
          const account = await AdminService.createAccount(
            Object.assign({}, this.editedAccount)
          )
          this.accounts.push(account)
          this.totalAccounts += 1
          this.close()
          this.snackSuccess('account created')
        } catch (error) {
          this.errors = { ...error.fields }
          this.$refs.accountForm.validate()
          this.snackError(error.message)
        }
      }
    },

    async deleteAccount(account) {
      if (
        await this.$root.$confirm('Delete', 'Are you sure?', {
          color: 'error'
        })
      ) {
        try {
          const index = this.accounts.indexOf(account)
          const id = this.accounts[index].id
          await AdminService.deleteAccount(id)
          this.accounts = this.accounts.filter(v => v.id !== id)
          this.totalAccounts--
          this.snackSuccess('account deleted')
        } catch (error) {
          this.snackError('could not delete account')
        }
      }
    },

    snackSuccess(message) {
      this.snack = true
      this.snackColor = 'success'
      this.snackText = message
    },
    snackError(message) {
      this.snack = true
      this.snackColor = 'error'
      this.snackText = message
    }
  }
}
</script>
